import { Button } from "@/components/ui/Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { cn } from "@/lib/utils"
import { GROUPS, SETTINGS, type SidebarGroup, type SidebarPage } from "../sidebarData"

type Props = { active: string; onSelect: (page: SidebarPage) => void; user: { name?: string; email: string } | null }

function GroupHeading({ group, first }: { group: SidebarGroup; first: boolean }) {
  if (!group.heading) return null
  const Icon = group.main
  if (Icon) {
    return (
      <div className={cn("flex items-center gap-2 px-2.5 pb-1.5 text-sm font-semibold text-foreground", first ? "pt-0" : "pt-5")}>
        <Icon size={16} strokeWidth={2.25} className="text-primary" />
        {group.heading}
      </div>
    )
  }
  return (
    <div className="px-2.5 pb-1 pt-5 text-[10px] font-semibold uppercase text-muted-foreground" style={{ letterSpacing: "1.2px" }}>
      {group.heading}
    </div>
  )
}

// Same list on every page: no market mode, no market list, no settings group.
// Settings live in the avatar menu at the bottom.
export default function Sidebar({ active, onSelect, user }: Props) {
  const name = user?.name || user?.email || "?"

  return (
    <div className="flex w-[220px] flex-shrink-0 flex-col border-r border-border bg-background">
      <div className="flex h-11 flex-shrink-0 items-center gap-2 border-b border-border px-4">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-foreground text-xs font-semibold text-background">A</div>
        <span className="text-sm font-semibold tracking-tight text-foreground">ATLAS AI</span>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2.5 py-2.5">
        {GROUPS.map((group, gi) => (
          <div key={gi}>
            <GroupHeading group={group} first={gi === 0} />
            {group.pages.map((p) => {
              const Icon = p.icon
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onSelect(p)}
                  className={cn(
                    "mb-0.5 flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors",
                    active === p.key
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                  )}
                >
                  <Icon size={16} strokeWidth={2} className="flex-shrink-0" />
                  {p.label}
                </button>
              )
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-auto w-full justify-start gap-2.5 rounded-none bg-transparent px-4 py-3">
              <div
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-[13px] font-semibold text-background"
                style={{ background: "linear-gradient(135deg, var(--chart-2), var(--secondary))" }}
              >
                {name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <div className="truncate text-[13px] font-medium text-foreground">{name}</div>
                {user?.name && <div className="truncate text-[10px] text-muted-foreground">{user.email}</div>}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="min-w-[200px]">
            <DropdownMenuLabel>Personal</DropdownMenuLabel>
            <DropdownMenuItem>Your profile</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Settings</DropdownMenuLabel>
            {SETTINGS.map((label) => <DropdownMenuItem key={label}>{label}</DropdownMenuItem>)}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive">Logout</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
